import React, { useCallback, useEffect, useRef, useState } from "react";
import { appCheckHeader } from "../utils/appCheck";

// ─────────────────────────────────────────────────────────────────────
// AI HELPER — shared module
//
// IMPORTANT — API PROXY SETUP:
// Direct browser calls to api.anthropic.com are blocked by CORS.
// This module calls your Firebase Cloud Function proxy instead.
//
// 1. Deploy functions/index.js (provided separately as functions_index.js)
// 2. Set AI_PROXY_URL below to your deployed function URL:
//    e.g. "https://us-central1-YOUR-PROJECT.cloudfunctions.net/anthropicProxy"
// 3. For local dev, use the emulator URL:
//    "http://127.0.0.1:5001/YOUR-PROJECT/us-central1/anthropicProxy"
//
// Exports: AiHelperButton  (read-only: advice + mind map)
//          AiInteractiveHelperButton  (interactive: can mutate data)
// ─────────────────────────────────────────────────────────────────────

// Firebase Function proxy URL. Must be set in .env.local as:
// REACT_APP_AI_PROXY_URL=https://us-central1-sft-site-management.cloudfunctions.net/anthropicProxy
const AI_PROXY_URL = (process.env.REACT_APP_AI_PROXY_URL || "").trim();

export const AI = {
  bg:        "#0A0F1E",
  surface:   "#111827",
  card:      "#1A2235",
  border:    "#1E3A5F",
  glow:      "#0EA5E9",
  glowSoft:  "rgba(14,165,233,0.12)",
  glowMid:   "rgba(14,165,233,0.25)",
  accent:    "#38BDF8",
  accentAlt: "#818CF8",
  green:     "#34D399",
  greenSoft: "rgba(52,211,153,0.15)",
  amber:     "#FB923C",
  amberSoft: "rgba(251,146,60,0.15)",
  text:      "#F0F6FF",
  textMuted: "#64748B",
  textDim:   "#94A3B8",
};

const NODE_COLORS = ["#818CF8","#34D399","#FB923C","#F472B6","#A78BFA","#FCD34D"];

// ── Single clean API call — always routes through the Firebase proxy ──────────
export async function aiChat(systemPrompt, userMessage, tools = []) {
  const body = {
    model:      "claude-sonnet-4-5",
    max_tokens: 1024,
    system:     systemPrompt,
    messages:   [{ role: "user", content: userMessage }],
  };
  if (tools.length) body.tools = tools;

  // Always use the server-side proxy — never expose the API key in the browser.
  // Set REACT_APP_AI_PROXY_URL in .env.local to your deployed function URL.
  // For local dev: http://127.0.0.1:5001/YOUR-PROJECT/us-central1/anthropicProxy
  const proxyUrl = AI_PROXY_URL;
  if (!proxyUrl) {
    throw new Error(
      "AI proxy not configured. Set REACT_APP_AI_PROXY_URL in .env.local to your Firebase Function URL."
    );
  }

  // Attach the Firebase ID token so the proxy can verify the caller is authenticated
  let idToken = "";
  try {
    const { getAuth } = await import("firebase/auth");
    const user = getAuth().currentUser;
    if (user) idToken = await user.getIdToken();
  } catch (_) { /* auth not available — proceed without token in dev */ }

  let res;
  try {
    res = await fetch(proxyUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...(await appCheckHeader()),
      },
      body: JSON.stringify(body),
    });
  } catch (networkError) {
    throw new Error(
      `Failed to reach AI proxy at ${proxyUrl}. Check that the Firebase Function is public/invokable and that CORS allows http://localhost:3000.`
    );
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || err?.error || `AI proxy HTTP ${res.status}`);
  }

  return res.json();
}

// Keep callAI as an alias
export const callAI = aiChat;

// ── Mind map parser ────────────────────────────────────────────────────────────
export function parseToMindMap(text, question) {
  const central  = question.length > 40 ? question.slice(0, 38) + "…" : question;
  const lines    = text.split("\n").map(l => l.trim()).filter(Boolean);
  const branches = []; let current = null;
  lines.forEach(line => {
    const isH  = /^(\*\*|#{1,3}|[0-9]+\.)/.test(line);
    const clean = line.replace(/^[#*\-•>\d.]+\s*/, "").replace(/\*\*/g, "").replace(/:$/, "").trim();
    if (!clean || clean.length < 2) return;
    if (isH && clean.length < 60) {
      if (current) branches.push(current);
      current = { label: clean.slice(0, 32), children: [] };
    } else if (current && current.children.length < 4) {
      current.children.push(clean.slice(0, 28));
    } else if (!current && branches.length < 6 && clean.length < 55) {
      branches.push({ label: clean.slice(0, 32), children: [] });
    }
  });
  if (current) branches.push(current);
  if (branches.length < 2) {
    (text.match(/[^.!?]+[.!?]+/g) || [text]).slice(0, 5).forEach(s => {
      const c = s.trim().replace(/\*\*/g, "").slice(0, 36);
      if (c.length > 8) branches.push({ label: c, children: [] });
    });
  }
  return { central, branches: branches.slice(0, 6) };
}

// ── Mind map canvas ────────────────────────────────────────────────────────────
export function AiMindMapCanvas({ data }) {
  const canvasRef   = useRef(null);
  const animRef     = useRef(null);
  const nodesRef    = useRef([]);
  const progressRef = useRef(0);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (!data || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const W      = canvas.offsetWidth  || 480;
    const H      = canvas.offsetHeight || 420;
    const dpr    = window.devicePixelRatio || 1;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    const cx = W / 2, cy = H / 2;
    const nodes = [], edges = [];
    nodes.push({ id: "root", label: data.central, x: cx, y: cy, r: 40, color: "#0EA5E9", level: 0 });
    (data.branches || []).forEach((b, bi) => {
      const angle = -Math.PI / 2 + bi * (Math.PI * 2 / data.branches.length);
      const r1    = Math.min(W, H) * 0.27;
      const col   = NODE_COLORS[bi % NODE_COLORS.length];
      const bx = cx + Math.cos(angle) * r1, by = cy + Math.sin(angle) * r1;
      nodes.push({ id: `b${bi}`, label: b.label, x: bx, y: by, r: 28, color: col, level: 1 });
      edges.push({ from: "root", to: `b${bi}`, color: col, w: 2 });
      (b.children || []).forEach((child, ci) => {
        const spread = Math.min(Math.PI * 0.55, (Math.PI * 0.85) / Math.max(b.children.length - 1, 1));
        const cAngle = (angle - spread * ((b.children.length - 1) / 2)) + ci * spread;
        const r2     = Math.min(W, H) * 0.46;
        const cid    = `b${bi}c${ci}`;
        nodes.push({ id: cid, label: child, x: cx + Math.cos(cAngle) * r2, y: cy + Math.sin(cAngle) * r2, r: 19, color: col, level: 2 });
        edges.push({ from: `b${bi}`, to: cid, color: col, w: 1.5, dashed: true });
      });
    });
    nodesRef.current    = nodes;
    progressRef.current = 0;
    const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));

    function frame() {
      progressRef.current = Math.min(1, progressRef.current + 0.018);
      const p = progressRef.current;
      ctx.clearRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(14,165,233,0.04)"; ctx.lineWidth = 1;
      for (let x = 0; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
      for (let y = 0; y < H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
      edges.forEach((e, ei) => {
        const from = nodeMap[e.from], to = nodeMap[e.to]; if (!from || !to) return;
        const ep = Math.max(0, Math.min(1, (p - ei * 0.055) * 2)); if (ep <= 0) return;
        const mx = from.x + (to.x - from.x) * ep, my = from.y + (to.y - from.y) * ep;
        ctx.save();
        if (e.dashed) ctx.setLineDash([4, 4]);
        const g = ctx.createLinearGradient(from.x, from.y, mx, my);
        g.addColorStop(0, e.color + "88"); g.addColorStop(1, e.color + "44");
        ctx.strokeStyle = g; ctx.lineWidth = e.w; ctx.shadowColor = e.color; ctx.shadowBlur = 6;
        ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(mx, my); ctx.stroke();
        ctx.restore(); ctx.setLineDash([]);
      });
      nodes.forEach(n => {
        const delay = n.level === 0 ? 0 : n.level === 1 ? 0.15 : 0.45;
        const np    = Math.max(0, Math.min(1, (p - delay) * 2.5)); if (np <= 0) return;
        const isHov = hovered === n.id;
        const r     = n.r * (0.4 + np * 0.6 + (isHov ? 0.12 : 0));
        ctx.save(); ctx.globalAlpha = np;
        const aura = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r * 2.8);
        aura.addColorStop(0, n.color + "30"); aura.addColorStop(1, "transparent");
        ctx.fillStyle = aura; ctx.beginPath(); ctx.arc(n.x, n.y, r * 2.8, 0, Math.PI * 2); ctx.fill();
        ctx.shadowColor = n.color; ctx.shadowBlur = isHov ? 22 : 12;
        const fill = ctx.createRadialGradient(n.x - r * .3, n.y - r * .3, 0, n.x, n.y, r);
        fill.addColorStop(0, n.color + "EE"); fill.addColorStop(1, n.color + "88");
        ctx.fillStyle = fill; ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = n.color; ctx.lineWidth = isHov ? 2.5 : 1.5; ctx.globalAlpha = np * 0.9; ctx.stroke();
        ctx.globalAlpha = np; ctx.shadowBlur = 0;
        ctx.fillStyle = "#FFF"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        const fs = n.level === 0 ? 11 : n.level === 1 ? 9 : 8;
        ctx.font = `${n.level === 0 ? 700 : 600} ${fs}px 'DM Sans',sans-serif`;
        const maxW = n.r * 3.2, words = n.label.split(" "), lines = []; let cur = "";
        words.forEach(w => {
          const test = cur ? cur + " " + w : w;
          if (ctx.measureText(test).width > maxW && cur) { lines.push(cur); cur = w; } else cur = test;
        });
        if (cur) lines.push(cur);
        const lh = fs + 3, sy = n.y - ((lines.length - 1) * lh) / 2;
        lines.forEach((l, li) => ctx.fillText(l, n.x, sy + li * lh));
        ctx.restore();
      });
      if (p < 1) animRef.current = requestAnimationFrame(frame);
    }
    animRef.current = requestAnimationFrame(frame);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [data, hovered]);

  const handleMouseMove = useCallback((e) => {
    if (!canvasRef.current || !nodesRef.current.length) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const hit = nodesRef.current.find(n => Math.hypot(n.x - mx, n.y - my) < n.r + 8);
    setHovered(hit ? hit.id : null);
  }, []);

  return (
    <canvas ref={canvasRef} onMouseMove={handleMouseMove} onMouseLeave={() => setHovered(null)}
      style={{ width: "100%", height: "100%", display: "block", cursor: hovered ? "pointer" : "default" }} />
  );
}

// ── Spinner ────────────────────────────────────────────────────────────────────
export function AiSpinner({ size = 16 }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size,
      border: `2px solid rgba(14,165,233,0.2)`, borderTopColor: "#38BDF8",
      borderRadius: "50%", animation: "ai-spin 0.7s linear infinite", flexShrink: 0,
    }} />
  );
}

// ── Shared answer renderer ─────────────────────────────────────────────────────
// Escape HTML so AI/user text can never inject markup; we then re-introduce
// ONLY the intended <strong> from **bold** on the already-escaped string.
const escapeAnswerHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function renderAnswerLines(text) {
  return String(text == null ? "" : text).split("\n").map((line, i) => {
    const isBold   = /^\*\*[^*]+\*\*/.test(line) || /^#{1,3}\s/.test(line);
    const isBullet = /^[-•*]\s/.test(line);
    const cleaned  = escapeAnswerHtml(line.replace(/^[#*\-•>\d.]+\s*/, "").trim())
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (!cleaned) return null;
    if (isBold)   return <p key={i} style={{ margin: "10px 0 3px", fontSize: 12, fontWeight: 700, color: AI.accent }} dangerouslySetInnerHTML={{ __html: cleaned }} />;
    if (isBullet) return <p key={i} style={{ margin: "2px 0", fontSize: 11.5, color: AI.textDim, paddingLeft: 14, position: "relative", lineHeight: 1.6 }}><span style={{ position: "absolute", left: 0, top: 4, fontSize: 7, color: AI.glow }}>◆</span><span dangerouslySetInnerHTML={{ __html: cleaned }} /></p>;
    return <p key={i} style={{ margin: "2px 0", fontSize: 11.5, color: AI.textDim, lineHeight: 1.65 }} dangerouslySetInnerHTML={{ __html: cleaned }} />;
  }).filter(Boolean);
}

// ── Overlay wrapper ────────────────────────────────────────────────────────────
export function AiOverlay({ onClose, children }) {
  return (
    <>
      <div style={{ position: "fixed", inset: 0, zIndex: 10000, backdropFilter: "blur(14px) brightness(0.35)", WebkitBackdropFilter: "blur(14px) brightness(0.35)", background: "rgba(5,10,20,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "ai-fadeIn 0.2s ease" }}
        onClick={onClose}>
        <div onClick={e => e.stopPropagation()}
          style={{ width: "min(560px,97vw)", height: "min(650px,92vh)", borderRadius: 20, overflow: "hidden", boxShadow: `0 0 0 1px #1E3A5F,0 40px 80px rgba(0,0,0,0.7),0 0 60px rgba(14,165,233,0.1)`, display: "flex", flexDirection: "column", animation: "ai-slideUp 0.28s cubic-bezier(0.34,1.56,0.64,1)" }}>
          {children}
        </div>
      </div>
      <style>{`@keyframes ai-spin{to{transform:rotate(360deg)}}@keyframes ai-fadeIn{from{opacity:0}to{opacity:1}}@keyframes ai-slideUp{from{opacity:0;transform:translateY(24px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
    </>
  );
}

// ── Trigger button ─────────────────────────────────────────────────────────────
export function AiTriggerBtn({ onClick, label = "AI Help" }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20, background: hov ? "rgba(14,165,233,0.1)" : "transparent", border: "1.5px solid #0EA5E9", color: "#38BDF8", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit", letterSpacing: "0.03em", transition: "all 0.2s", boxShadow: hov ? "0 0 18px rgba(14,165,233,0.3)" : "0 0 8px rgba(14,165,233,0.15)", whiteSpace: "nowrap" }}>
      <span style={{ fontSize: 13 }}>✦</span> {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// READ-ONLY AI HELPER (advice + mind map only)
// ─────────────────────────────────────────────────────────────────────────────
function AiAdvicePanel({ sectionName, sectionContext, suggestions = [], onClose }) {
  const [question, setQuestion] = useState("");
  const [answer,   setAnswer]   = useState("");
  const [mindMap,  setMindMap]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [view,     setView]     = useState("chat");
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 80); }, []);

  async function handleAsk(q = question) {
    if (!q.trim() || loading) return;
    setQuestion(q);
    setLoading(true); setError(""); setAnswer(""); setMindMap(null); setView("chat");
    const sys = `You are an expert construction contracts advisor (JBCC, NEC, GCC, FIDIC).
User is in the "${sectionName}" section of a contract management system.${sectionContext ? `\nContext: ${sectionContext}` : ""}
Reply with structured insight using **bold headings** and bullets. Max 350 words. Use 4–6 headings for mind map visualisation.`;
    try {
      const data = await aiChat(sys, q.trim());
      if (data.error) throw new Error(data.error.message);
      const text = (data.content || []).map(b => b.text || "").join("");
      setAnswer(text);
      setMindMap(parseToMindMap(text, q));
    } catch (e) {
      setError(`Error: ${e.message}`);
    } finally { setLoading(false); }
  }

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setView(id)} style={{ padding: "4px 11px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "inherit", background: view === id ? AI.glow : AI.border, color: view === id ? "#fff" : AI.textMuted, transition: "all 0.15s" }}>{label}</button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "'DM Sans','Helvetica Neue',sans-serif", background: AI.surface }}>
      {/* Header */}
      <div style={{ padding: "13px 18px 11px", borderBottom: `1px solid ${AI.border}`, background: AI.card, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: `radial-gradient(circle at 40% 40%,${AI.glow},${AI.accentAlt})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, boxShadow: `0 0 16px ${AI.glowMid}`, flexShrink: 0 }}>✦</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: AI.text }}>AI Contract Advisor</p>
          <p style={{ margin: 0, fontSize: 10, color: AI.textMuted }}>{sectionName}</p>
        </div>
        {mindMap && <div style={{ display: "flex", gap: 4 }}><TabBtn id="chat" label="Answer" /><TabBtn id="map" label="Mind Map" /></div>}
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: AI.textMuted, fontSize: 16, lineHeight: 1, padding: "3px 5px", borderRadius: 6, flexShrink: 0 }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "hidden", position: "relative", minHeight: 0 }}>
        <div style={{ position: "absolute", inset: 0, overflowY: "auto", padding: "14px 18px", display: view === "chat" ? "block" : "none" }}>
          {!answer && !loading && !error && (
            <div style={{ textAlign: "center", paddingTop: 20 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✦</div>
              <p style={{ color: AI.textMuted, fontSize: 12, lineHeight: 1.7, margin: 0 }}>Ask anything about this section.</p>
              <div style={{ marginTop: 14, display: "grid", gap: 6 }}>
                {suggestions.map(q => <button key={q} onClick={() => handleAsk(q)} style={{ background: AI.card, border: `1px solid ${AI.border}`, borderRadius: 8, padding: "8px 12px", color: AI.textDim, fontSize: 11, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>💬 {q}</button>)}
              </div>
            </div>
          )}
          {loading && <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, paddingTop: 30 }}><AiSpinner /><p style={{ color: AI.textMuted, fontSize: 12, margin: 0 }}>Thinking…</p></div>}
          {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#2A1215", border: "1px solid #7F1D1D", color: "#FCA5A5", fontSize: 12 }}>{error}</div>}
          {answer && !loading && (
            <div>
              <div style={{ background: AI.glowSoft, border: `1px solid ${AI.border}`, borderRadius: "12px 12px 3px 12px", padding: "8px 12px", marginBottom: 14, fontSize: 12, color: AI.accent, fontWeight: 600 }}>{question}</div>
              <div>{renderAnswerLines(answer)}</div>
              <button onClick={() => setView("map")} style={{ marginTop: 14, width: "100%", padding: "9px", background: `linear-gradient(135deg,${AI.glow}22,${AI.accentAlt}22)`, border: `1px solid ${AI.border}`, borderRadius: 8, color: AI.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>◎ View as Mind Map</button>
            </div>
          )}
        </div>
        <div style={{ position: "absolute", inset: 0, display: view === "map" ? "block" : "none" }}>
          {mindMap && <AiMindMapCanvas data={mindMap} />}
        </div>
      </div>

      {/* Input */}
      <div style={{ padding: "10px 14px 12px", borderTop: `1px solid ${AI.border}`, background: AI.card, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea ref={inputRef} value={question} onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAsk(); } }}
            placeholder="Ask about this section… (Enter to send)" rows={2}
            style={{ flex: 1, resize: "none", background: AI.bg, border: `1px solid ${AI.border}`, borderRadius: 10, padding: "8px 12px", color: AI.text, fontSize: 12, fontFamily: "inherit", outline: "none", lineHeight: 1.5 }}
            onFocus={e => e.target.style.borderColor = AI.glow} onBlur={e => e.target.style.borderColor = AI.border}
          />
          <button onClick={() => handleAsk()} disabled={!question.trim() || loading}
            style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: question.trim() && !loading ? `linear-gradient(135deg,${AI.glow},${AI.accentAlt})` : AI.border, color: "#fff", cursor: question.trim() && !loading ? "pointer" : "not-allowed", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {loading ? <AiSpinner /> : "↑"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AiHelperButton({ sectionName, sectionContext = "", suggestions = [] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <AiTriggerBtn onClick={() => setOpen(true)} />
      {open && <AiOverlay onClose={() => setOpen(false)}><AiAdvicePanel sectionName={sectionName} sectionContext={sectionContext} suggestions={suggestions} onClose={() => setOpen(false)} /></AiOverlay>}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INTERACTIVE AI HELPER — can call tools that mutate app data
// Props:
//   sectionName, sectionContext, suggestions  — same as above
//   tools: [{ name, description, input_schema, handler(input) => { summary, changes } }]
//   onApplyChanges(changes) — called when AI proposes + user confirms changes
// ─────────────────────────────────────────────────────────────────────────────
function AiInteractivePanel({ sectionName, sectionContext, suggestions = [], tools = [], onApplyChanges, onClose }) {
  const [question,  setQuestion]  = useState("");
  const [messages,  setMessages]  = useState([]); // { role, text, type, pending }
  const [mindMap,   setMindMap]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [view,      setView]      = useState("chat");
  const [pending,   setPending]   = useState(null); // { summary, changes, toolName }
  const inputRef  = useRef(null);
  const bodyRef   = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 80); }, []);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages, loading]);

  const toolDefs = tools.map(t => ({
    name:         t.name,
    description:  t.description,
    input_schema: t.input_schema,
  }));

  async function handleAsk(q = question) {
    if (!q.trim() || loading) return;
    setQuestion("");
    const userMsg = q.trim();
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);
    setPending(null);

    const sys = `You are an expert construction contracts AI assistant integrated into a contract management system.
Section: "${sectionName}"
${sectionContext || ""}

CRITICAL RULES FOR DATA UPDATES:
- You have the FULL item list in the context above. Read it carefully before deciding which items to update.
- Items "starting with A that have no category" means: itemNo starts with A AND current CPA shows as NONE.
- NEVER update items that already have a CPA class unless the user explicitly says "replace", "override", or "change all".
- When the user says "no category" / "unassigned" / "missing CPA" → always set noCpaOnly=true.
- Always state exactly how many items will be changed and list the item numbers if fewer than 10.

You can BOTH give advice AND perform actions using the available tools.
- If the user asks a question → answer with **bold headings** and bullets (4-6 headings, under 300 words).
- If the user asks to DO something (update, change, set, bulk edit) → use the appropriate tool.
- Always confirm what you did clearly after using a tool.`;

    try {
      const data = await aiChat(sys, userMsg, toolDefs.length ? toolDefs : []);
      if (data.error) throw new Error(data.error.message);

      // Check for tool use
      const toolUseBlock = (data.content || []).find(b => b.type === "tool_use");
      const textBlock    = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("").trim();

      if (toolUseBlock) {
        // Find the matching tool handler
        const toolDef = tools.find(t => t.name === toolUseBlock.name);
        if (toolDef) {
          const result = await toolDef.handler(toolUseBlock.input);
          // Show AI's intent text if any
          if (textBlock) {
            setMessages(prev => [...prev, { role: "assistant", text: textBlock }]);
          }
          // Store pending action for user confirmation
          setPending({ summary: result.summary, changes: result.changes, toolName: toolUseBlock.name, count: result.count });
          setMessages(prev => [...prev, { role: "action", summary: result.summary, count: result.count, changes: result.changes }]);
          setMindMap(null);
        } else {
          setMessages(prev => [...prev, { role: "assistant", text: "I tried to use a tool that isn't available." }]);
        }
      } else if (textBlock) {
        setMessages(prev => [...prev, { role: "assistant", text: textBlock }]);
        setMindMap(parseToMindMap(textBlock, userMsg));
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: "error", text: `Error: ${e.message}` }]);
    } finally {
      setLoading(false);
    }
  }

  function applyPending() {
    if (!pending || typeof onApplyChanges !== "function") return;
    onApplyChanges(pending.changes);
    setMessages(prev => prev.map(m => m.role === "action" ? { ...m, applied: true } : m));
    setPending(null);
  }

  function discardPending() {
    setMessages(prev => prev.map(m => m.role === "action" ? { ...m, discarded: true } : m));
    setPending(null);
  }

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setView(id)} style={{ padding: "4px 11px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "inherit", background: view === id ? AI.glow : AI.border, color: view === id ? "#fff" : AI.textMuted }}>{label}</button>
  );

  function MessageBubble({ msg }) {
    if (msg.role === "user") return (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <div style={{ maxWidth: "80%", background: AI.glowSoft, border: `1px solid ${AI.border}`, borderRadius: "12px 12px 3px 12px", padding: "8px 12px", fontSize: 12, color: AI.accent, fontWeight: 600 }}>{msg.text}</div>
      </div>
    );

    if (msg.role === "assistant") return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: `radial-gradient(circle at 40% 40%,${AI.glow},${AI.accentAlt})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, flexShrink: 0, marginTop: 2 }}>✦</div>
          <div style={{ flex: 1 }}>{renderAnswerLines(msg.text)}</div>
        </div>
        {mindMap && (
          <button onClick={() => setView("map")} style={{ marginTop: 8, padding: "7px 12px", background: `linear-gradient(135deg,${AI.glow}22,${AI.accentAlt}22)`, border: `1px solid ${AI.border}`, borderRadius: 8, color: AI.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>◎ View as Mind Map</button>
        )}
      </div>
    );

    if (msg.role === "action") return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ background: msg.applied ? AI.greenSoft : msg.discarded ? "rgba(100,116,139,0.1)" : AI.amberSoft, border: `1px solid ${msg.applied ? AI.green : msg.discarded ? AI.border : AI.amber}`, borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 16 }}>{msg.applied ? "✅" : msg.discarded ? "✕" : "⚡"}</span>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: msg.applied ? AI.green : msg.discarded ? AI.textMuted : AI.amber }}>
              {msg.applied ? "Changes applied" : msg.discarded ? "Discarded" : "Proposed changes"}
            </p>
            {msg.count !== undefined && <span style={{ fontSize: 10, background: msg.applied ? AI.green : AI.amber, color: "#fff", padding: "2px 7px", borderRadius: 10, fontWeight: 700 }}>{msg.count} items</span>}
          </div>
          <p style={{ margin: "0 0 10px", fontSize: 11.5, color: AI.textDim, lineHeight: 1.5 }}>{msg.summary}</p>
          {!msg.applied && !msg.discarded && (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={applyPending} style={{ flex: 1, padding: "8px", background: `linear-gradient(135deg,${AI.green},#059669)`, border: "none", borderRadius: 8, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>✓ Apply Changes</button>
              <button onClick={discardPending} style={{ padding: "8px 14px", background: "transparent", border: `1px solid ${AI.border}`, borderRadius: 8, color: AI.textMuted, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Discard</button>
            </div>
          )}
        </div>
      </div>
    );

    if (msg.role === "error") return (
      <div style={{ marginBottom: 10, padding: "10px 14px", borderRadius: 8, background: "#2A1215", border: "1px solid #7F1D1D", color: "#FCA5A5", fontSize: 12 }}>{msg.text}</div>
    );

    return null;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "'DM Sans','Helvetica Neue',sans-serif", background: AI.surface }}>
      {/* Header */}
      <div style={{ padding: "13px 18px 11px", borderBottom: `1px solid ${AI.border}`, background: AI.card, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: `radial-gradient(circle at 40% 40%,${AI.glow},${AI.accentAlt})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, boxShadow: `0 0 16px ${AI.glowMid}`, flexShrink: 0 }}>✦</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: AI.text }}>AI Contract Assistant</p>
          <p style={{ margin: 0, fontSize: 10, color: AI.textMuted }}>{sectionName} · Interactive</p>
        </div>
        {mindMap && <div style={{ display: "flex", gap: 4 }}><TabBtn id="chat" label="Chat" /><TabBtn id="map" label="Mind Map" /></div>}
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: AI.textMuted, fontSize: 16, lineHeight: 1, padding: "3px 5px", borderRadius: 6, flexShrink: 0 }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "hidden", position: "relative", minHeight: 0 }}>
        {/* Chat */}
        <div ref={bodyRef} style={{ position: "absolute", inset: 0, overflowY: "auto", padding: "14px 18px", display: view === "chat" ? "block" : "none" }}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", paddingTop: 20 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✦</div>
              <p style={{ color: AI.textMuted, fontSize: 12, lineHeight: 1.7, margin: "0 0 4px" }}>Ask questions <em>or give instructions</em> — I can update data directly.</p>
              <p style={{ color: AI.textMuted, fontSize: 11, margin: "0 0 14px" }}>e.g. <em style={{ color: AI.accent }}>"Set CPA class to A10 for all items between A100–A200"</em></p>
              <div style={{ display: "grid", gap: 6 }}>
                {suggestions.map(q => <button key={q} onClick={() => handleAsk(q)} style={{ background: AI.card, border: `1px solid ${AI.border}`, borderRadius: 8, padding: "8px 12px", color: AI.textDim, fontSize: 11, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>💬 {q}</button>)}
              </div>
            </div>
          )}
          {messages.map((msg, i) => <MessageBubble key={i} msg={msg} />)}
          {loading && <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}><div style={{ width: 22, height: 22, borderRadius: "50%", background: `radial-gradient(circle at 40% 40%,${AI.glow},${AI.accentAlt})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>✦</div><AiSpinner /></div>}
        </div>
        {/* Map */}
        <div style={{ position: "absolute", inset: 0, display: view === "map" ? "block" : "none" }}>
          {mindMap && <AiMindMapCanvas data={mindMap} />}
        </div>
      </div>

      {/* Input */}
      <div style={{ padding: "10px 14px 12px", borderTop: `1px solid ${AI.border}`, background: AI.card, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea ref={inputRef} value={question} onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAsk(); } }}
            placeholder="Ask a question or give an instruction… (Enter to send)" rows={2}
            style={{ flex: 1, resize: "none", background: AI.bg, border: `1px solid ${AI.border}`, borderRadius: 10, padding: "8px 12px", color: AI.text, fontSize: 12, fontFamily: "inherit", outline: "none", lineHeight: 1.5 }}
            onFocus={e => e.target.style.borderColor = AI.glow} onBlur={e => e.target.style.borderColor = AI.border}
          />
          <button onClick={() => handleAsk()} disabled={!question.trim() || loading}
            style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: question.trim() && !loading ? `linear-gradient(135deg,${AI.glow},${AI.accentAlt})` : AI.border, color: "#fff", cursor: question.trim() && !loading ? "pointer" : "not-allowed", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {loading ? <AiSpinner /> : "↑"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AiInteractiveHelperButton({ sectionName, sectionContext = "", suggestions = [], tools = [], onApplyChanges, label = "AI Help" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <AiTriggerBtn onClick={() => setOpen(true)} label={label} />
      {open && (
        <AiOverlay onClose={() => setOpen(false)}>
          <AiInteractivePanel
            sectionName={sectionName}
            sectionContext={sectionContext}
            suggestions={suggestions}
            tools={tools}
            onApplyChanges={changes => { if (onApplyChanges) onApplyChanges(changes); }}
            onClose={() => setOpen(false)}
          />
        </AiOverlay>
      )}
    </>
  );
}